<template>
  <div v-if="visible" class="robot-action-modal" @click.self="close">
    <section
      class="robot-action-modal__panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="robot-action-title"
    >
      <header class="robot-action-modal__header">
        <div>
          <h2 id="robot-action-title">{{ title }}</h2>
          <p>{{ subtitle }}</p>
        </div>
        <button type="button" :disabled="submitting" aria-label="关闭" @click="close">
          <i class="el-icon-close" />
        </button>
      </header>

      <div class="robot-action-summary">
        <span :style="{ background: robot.color || '#1f6bff' }" />
        <div>
          <strong>{{ robot.name }}</strong>
          <small>{{ robot.exchange }} · {{ robot.symbol }}</small>
        </div>
        <em :class="robot.status">{{ statusText }}</em>
      </div>

      <template v-if="action === 'modify'">
        <template v-if="productType === 'bb'">
          <div class="robot-action-grid">
            <div class="robot-action-field">
              <label>{{ firstCoin }} 仓位</label>
              <div class="robot-action-input-unit">
                <input v-model.trim="form.amountOne" inputmode="decimal" placeholder="请输入修改后仓位" />
                <span>{{ firstCoin }}</span>
              </div>
            </div>
            <div class="robot-action-field">
              <label>{{ secondCoin }} 仓位</label>
              <div class="robot-action-input-unit">
                <input v-model.trim="form.amountTwo" inputmode="decimal" placeholder="请输入修改后仓位" />
                <span>{{ secondCoin }}</span>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="productType === 'martin'">
          <div class="robot-action-grid">
            <div class="robot-action-field">
              <label>策略类型</label>
              <select v-model.number="form.strategyType">
                <option :value="0">仓位马丁策略</option>
                <option :value="1">盈利预期马丁策略</option>
                <option :value="2">双马丁策略</option>
              </select>
            </div>
            <div class="robot-action-field">
              <label>趋势周期</label>
              <select v-model.number="form.trendCycle">
                <option :value="1">1 分钟</option>
                <option :value="5">5 分钟</option>
                <option :value="15">15 分钟</option>
                <option :value="30">30 分钟</option>
              </select>
            </div>
            <div class="robot-action-field">
              <label>首单额度 (USDT)</label>
              <input v-model.trim="form.firstSpace" inputmode="decimal" />
            </div>
            <div class="robot-action-field">
              <label>循环次数</label>
              <input v-model.trim="form.cycleNumber" inputmode="numeric" />
            </div>
            <div class="robot-action-field">
              <label>首单倍数</label>
              <input v-model.trim="form.firstMultiple" inputmode="decimal" />
            </div>
            <div class="robot-action-field">
              <label>做单数量</label>
              <input v-model.trim="form.billNumber" inputmode="numeric" />
            </div>
            <div class="robot-action-field">
              <label>止盈比例 (%)</label>
              <input v-model.trim="form.stopRatio" inputmode="decimal" />
            </div>
            <div class="robot-action-field">
              <label>止盈回调 (%)</label>
              <input v-model.trim="form.stopCallback" inputmode="decimal" />
            </div>
          </div>
          <div class="robot-action-field">
            <label>满单止损比例 (%)</label>
            <input v-model.trim="form.fullStopRatio" inputmode="decimal" />
          </div>
        </template>

        <template v-else>
          <div class="robot-action-field">
            <label>投入金额 (USDT)</label>
            <div class="robot-action-input-unit">
              <input v-model.trim="form.amount" inputmode="decimal" placeholder="请输入修改后的投入金额" />
              <span>USDT</span>
            </div>
            <small>当前投入：{{ robot.investment }} USDT</small>
          </div>

          <div v-if="showHedgeStopLoss" class="robot-action-field">
            <label>止损设置</label>
            <div class="robot-action-options">
              <button
                v-for="item in stopLossOptions"
                :key="item.value"
                :class="{ active: Number(form.stopLoss) === item.value }"
                type="button"
                @click="form.stopLoss = item.value"
              >
                {{ item.label }}
              </button>
            </div>
          </div>

          <div
            v-if="showHedgeStopLoss && Number(form.stopLoss) > 0"
            class="robot-action-field"
          >
            <label>补亏损比例 (%)</label>
            <input v-model.trim="form.lossTransferRate" inputmode="decimal" />
          </div>

          <div v-if="['hedge', 'term'].includes(productType)" class="robot-action-field">
            <div class="robot-action-label-row">
              <label>最大持仓设置</label>
              <label class="robot-action-check">
                <input v-model="form.infinite" type="checkbox" />
                无限
              </label>
            </div>
            <div v-if="!form.infinite" class="robot-action-input-unit">
              <input v-model.trim="form.maxPosition" inputmode="decimal" placeholder="不得小于投入金额" />
              <span>USDT</span>
            </div>
            <div v-else class="robot-action-infinite">无限大</div>
          </div>
        </template>

      </template>

      <template v-else-if="action === 'delete'">
        <div class="robot-action-warning danger">
          <i class="el-icon-warning-outline" />
          <div>
            <strong>确认删除当前策略？</strong>
            <p>删除后机器人将不再监控该策略，未撤销的挂单和持仓可能仍需在交易所处理。</p>
          </div>
        </div>

        <div v-if="showDeleteOrderChoice" class="robot-action-delete-options">
          <label>
            <input v-model="form.orderMode" type="radio" :value="deleteWithoutOrderValue" />
            <span>
              <strong>删除策略，不撤销挂单</strong>
              <small>保留交易所当前挂单和持仓，由你手动处理。</small>
            </span>
          </label>
          <label>
            <input v-model="form.orderMode" type="radio" :value="deleteWithOrderValue" />
            <span>
              <strong>删除策略并撤销挂单</strong>
              <small>同时撤销当前挂单，已成交订单不受影响。</small>
            </span>
          </label>
        </div>
      </template>

      <template v-else>
        <div class="robot-action-warning" :class="action">
          <i :class="action === 'resume' ? 'el-icon-video-play' : 'el-icon-video-pause'" />
          <div>
            <strong>{{ action === 'resume' ? '确认恢复当前策略？' : '确认暂停当前策略？' }}</strong>
            <p v-if="action === 'resume'">恢复后机器人会继续按照当前参数监控并执行交易。</p>
            <p v-else>暂停后不会再创建新的委托，现有挂单和持仓按交易所实际状态保留。</p>
          </div>
        </div>

        <div v-if="action === 'pause' && productType === 'hedge'" class="robot-action-field">
          <label class="robot-action-check">
            <input v-model="form.confirmPauseRisk" type="checkbox" />
            我已了解暂停策略可能影响当前挂单与持仓
          </label>
        </div>

        <div v-if="resumeNeedsMaximum" class="robot-action-field">
          <div class="robot-action-label-row">
            <label>恢复后的最大持仓</label>
            <label class="robot-action-check">
              <input v-model="form.infinite" type="checkbox" />
              无限
            </label>
          </div>
          <div v-if="!form.infinite" class="robot-action-input-unit">
            <input v-model.trim="form.maxPosition" inputmode="decimal" placeholder="请输入最大持仓" />
            <span>USDT</span>
          </div>
          <div v-else class="robot-action-infinite">无限大</div>
        </div>
      </template>

      <footer class="robot-action-modal__footer">
        <button type="button" :disabled="submitting" @click="close">取消</button>
        <button
          type="button"
          :class="confirmTone"
          :disabled="submitting"
          @click="submit"
        >
          {{ submitting ? '处理中…' : confirmText }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import {
  defaultRobotActionForm,
  robotProductType
} from '@/api/robot-actions'

function finiteNumber(value) {
  if (value === '' || value === null || value === undefined) return null
  const result = Number(value)
  return Number.isFinite(result) ? result : null
}

export default {
  name: 'RobotStrategyActionModal',
  props: {
    visible: { type: Boolean, default: false },
    action: { type: String, default: 'modify' },
    robot: { type: Object, default: () => ({ raw: {} }) },
    submitting: { type: Boolean, default: false }
  },
  data() {
    return {
      form: defaultRobotActionForm(this.robot, this.action)
    }
  },
  computed: {
    productType() {
      return robotProductType(this.robot)
    },
    title() {
      if (this.action === 'delete') return this.$t('删除机器人策略')
      if (this.action === 'pause') return this.$t('暂停机器人策略')
      if (this.action === 'resume') return this.$t('恢复机器人策略')
      return this.$t('修改机器人策略')
    },
    subtitle() {
      if (this.action === 'modify') return this.$t('修改后新参数将立即生效')
      return this.$t('请确认本次策略操作')
    },
    statusText() {
      if (this.robot.status === 'running') return this.$t('运行中')
      if (this.robot.status === 'paused') return this.$t('已暂停')
      return this.$t('状态未知')
    },
    confirmText() {
      if (this.action === 'delete') return this.$t('确认删除')
      if (this.action === 'pause') return this.$t('确认暂停')
      if (this.action === 'resume') return this.$t('确认恢复')
      return this.$t('确认修改')
    },
    confirmTone() {
      if (this.action === 'delete') return 'danger'
      if (this.action === 'resume') return 'success'
      return 'primary'
    },
    raw() {
      return this.robot.raw || {}
    },
    firstCoin() {
      return this.raw.coin_one || this.$t('币种一')
    },
    secondCoin() {
      return this.raw.coin_two || this.$t('币种二')
    },
    stopLossOptions() {
      const version = String(this.raw.version || '').toLowerCase()
      if (version === 'oneway') {
        return [
          { value: 100, label: '止损' },
          { value: 0, label: '不止损' }
        ]
      }
      if (String(this.raw.coin_two || '').toUpperCase() === 'ICP') {
        return [{ value: 100, label: this.$t('止损') }]
      }
      return [
        { value: 100, label: '止损' },
        { value: 5, label: '部分止损' },
        { value: 0, label: '不止损' }
      ]
    },
    showHedgeStopLoss() {
      return (
        this.productType === 'hedge' &&
        String(this.raw.version || '').toLowerCase() !== 'old'
      )
    },
    resumeNeedsMaximum() {
      return (
        this.action === 'resume' &&
        ['hedge', 'term'].includes(this.productType) &&
        Number(this.raw.stop_reason) === 1
      )
    },
    showDeleteOrderChoice() {
      return (
        Number(this.raw.__robotState) !== 2 &&
        ['bb', 'grid', 'martin', 'term'].includes(this.productType)
      )
    },
    deleteWithoutOrderValue() {
      return this.productType === 'term' ? '1' : '2'
    },
    deleteWithOrderValue() {
      return this.productType === 'term' ? '2' : '3'
    }
  },
  watch: {
    visible(value) {
      if (value) this.form = defaultRobotActionForm(this.robot, this.action)
    },
    robot() {
      if (this.visible) this.form = defaultRobotActionForm(this.robot, this.action)
    }
  },
  methods: {
    close() {
      if (!this.submitting) this.$emit('update:visible', false)
    },
    validateModify() {
      if (this.productType === 'bb') {
        const one = finiteNumber(this.form.amountOne)
        const two = finiteNumber(this.form.amountTwo)
        const oneRequired = finiteNumber(this.raw.space) > 0
        const twoRequired = finiteNumber(this.raw.space_two) > 0
        if (oneRequired && (one === null || one <= 0)) {
          this.$message.warning(this.$t(`请输入有效的 ${this.firstCoin} 仓位`))
          return false
        }
        if (twoRequired && (two === null || two <= 0)) {
          this.$message.warning(this.$t(`请输入有效的 ${this.secondCoin} 仓位`))
          return false
        }
        if (!oneRequired && !twoRequired && (one === null || one <= 0) && (two === null || two <= 0)) {
          this.$message.warning(this.$t('请至少填写一侧有效仓位'))
          return false
        }
        const minimumOne = finiteNumber(this.raw.__minPositionOne)
        const minimumTwo = finiteNumber(this.raw.__minPositionTwo)
        if (oneRequired && minimumOne !== null && one < minimumOne) {
          this.$message.warning(this.$t(`${this.firstCoin} 仓位不能低于 ${minimumOne}`))
          return false
        }
        if (twoRequired && minimumTwo !== null && two < minimumTwo) {
          this.$message.warning(this.$t(`${this.secondCoin} 仓位不能低于 ${minimumTwo}`))
          return false
        }
        return true
      }

      if (this.productType === 'martin') {
        const required = [
          this.form.firstSpace,
          this.form.cycleNumber,
          this.form.firstMultiple,
          this.form.billNumber,
          this.form.stopRatio,
          this.form.stopCallback,
          this.form.fullStopRatio
        ].map(finiteNumber)
        if (required.some(value => value === null)) {
          this.$message.warning(this.$t('请完整填写马丁策略参数'))
          return false
        }
        if (required[0] <= 0 || required[2] <= 0 || required[3] < 2 || required[3] > 32) {
          this.$message.warning(this.$t('马丁策略金额、倍数或做单数量不在有效范围'))
          return false
        }
        return true
      }

      const amount = finiteNumber(this.form.amount)
      if (amount === null || amount <= 0) {
        this.$message.warning(this.$t('请输入有效的投入金额'))
        return false
      }
      if (this.productType === 'astra' && amount < 100) {
        this.$message.warning(this.$t('Astra 策略投入金额不能低于 100 USDT'))
        return false
      }
      if (['hedge', 'term'].includes(this.productType) && !this.form.infinite) {
        const maximum = finiteNumber(this.form.maxPosition)
        if (maximum === null || maximum < amount) {
          this.$message.warning(this.$t('最大持仓不能小于投入金额'))
          return false
        }
      }
      return true
    },
    submit() {
      if (this.action === 'modify' && !this.validateModify()) return
      if (this.action === 'pause' && this.productType === 'hedge' && !this.form.confirmPauseRisk) {
        this.$message.warning(this.$t('请先确认已了解暂停策略的风险'))
        return
      }
      if (this.resumeNeedsMaximum && !this.form.infinite) {
        const maximum = finiteNumber(this.form.maxPosition)
        if (maximum === null || maximum <= 0) {
          this.$message.warning(this.$t('请输入有效的最大持仓金额'))
          return
        }
      }
      this.$emit('submit', {
        action: this.action,
        robot: this.robot,
        form: { ...this.form }
      })
    }
  }
}
</script>

<style>
.robot-action-modal {
  position: fixed;
  z-index: 155;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(20,17,38,.46);
  animation: up-fade-in .18s ease;
}

.robot-action-modal__panel {
  width: 520px;
  max-width: 92vw;
  max-height: 84vh;
  overflow-y: auto;
  padding: 28px;
  color: #1b1f2a;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 64px rgba(20,16,45,.28);
  animation: robot-action-pop .2s ease;
}

@keyframes robot-action-pop {
  from { opacity: 0; transform: translateY(10px) scale(.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.robot-action-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.robot-action-modal__header h2,
.robot-action-modal__header p {
  margin: 0;
}

.robot-action-modal__header h2 {
  font-size: 18px;
  font-weight: 800;
}

.robot-action-modal__header p {
  margin-top: 4px;
  color: #8a90a0;
  font-size: 12px;
}

.robot-action-modal__header > button {
  display: grid;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  place-items: center;
  color: #8a90a0;
  border: 0;
  border-radius: 8px;
  background: transparent;
}

.robot-action-modal__header > button:hover {
  background: #f5f6f9;
}

.robot-action-summary {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 18px;
  padding: 13px 14px;
  border: 1px solid #eef0f5;
  border-radius: 10px;
  background: #f8f9fb;
}

.robot-action-summary > span {
  width: 9px;
  height: 36px;
  flex: 0 0 auto;
  border-radius: 5px;
}

.robot-action-summary > div {
  min-width: 0;
  flex: 1;
}

.robot-action-summary strong,
.robot-action-summary small {
  display: block;
}

.robot-action-summary strong {
  overflow: hidden;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.robot-action-summary small {
  margin-top: 3px;
  color: #8a90a0;
  font-size: 11.5px;
}

.robot-action-summary em {
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11.5px;
  font-style: normal;
  font-weight: 700;
}

.robot-action-summary em.running {
  color: #0b7a47;
  background: #e8f6ee;
}

.robot-action-summary em.paused,
.robot-action-summary em.unknown {
  color: #5a6072;
  background: #eef0f5;
}

.robot-action-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.robot-action-field {
  margin-bottom: 13px;
}

.robot-action-field > label,
.robot-action-label-row > label:first-child {
  display: block;
  margin-bottom: 7px;
  color: #5a6072;
  font-size: 13.5px;
  font-weight: 600;
}

.robot-action-field input:not([type="checkbox"]):not([type="radio"]),
.robot-action-field select {
  width: 100%;
  height: 42px;
  outline: 0;
  color: #1b1f2a;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  padding: 0 13px;
  background: #fafbfd;
  font-size: 14px;
}

.robot-action-field input:focus,
.robot-action-field select:focus {
  border-color: #7aa7ff;
  box-shadow: 0 0 0 3px rgba(31,107,255,.1);
}

.robot-action-field > small {
  display: block;
  margin-top: 6px;
  color: #8a90a0;
  font-size: 11px;
}

.robot-action-input-unit {
  position: relative;
}

.robot-action-input-unit input {
  padding-right: 72px !important;
}

.robot-action-input-unit > span {
  position: absolute;
  top: 50%;
  right: 13px;
  color: #8a90a0;
  font-size: 12px;
  transform: translateY(-50%);
}

.robot-action-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.robot-action-options button {
  height: 36px;
  color: #5a6072;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background: #fff;
  font-size: 12px;
}

.robot-action-options button.active {
  color: #1f6bff;
  border-color: #8cb3ff;
  background: #eef4ff;
  font-weight: 700;
}

.robot-action-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.robot-action-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 7px;
  color: #5a6072;
  font-size: 12px;
}

.robot-action-infinite {
  height: 42px;
  padding: 0 13px;
  color: #5a6072;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background: #f7f8fb;
  font-size: 12px;
  line-height: 42px;
}

.robot-action-notice {
  margin-top: 4px;
  padding: 10px 13px;
  color: #0b7a47;
  border-radius: 8px;
  background: #eef9f3;
  font-size: 12px;
  line-height: 1.55;
}

.robot-action-warning {
  display: flex;
  gap: 12px;
  padding: 15px;
  color: #5a6072;
  border: 1px solid #dfe8fd;
  border-radius: 10px;
  background: #f3f7fe;
}

.robot-action-warning.resume {
  color: #0b7a47;
  border-color: #cdebd9;
  background: #eef9f3;
}

.robot-action-warning.danger {
  color: #9f2f35;
  border-color: #f2d2d5;
  background: #fff6f6;
}

.robot-action-warning > i {
  margin-top: 2px;
  font-size: 20px;
}

.robot-action-warning strong,
.robot-action-warning p {
  display: block;
  margin: 0;
}

.robot-action-warning strong {
  font-size: 14px;
}

.robot-action-warning p {
  margin-top: 5px;
  font-size: 12px;
  line-height: 1.65;
}

.robot-action-delete-options {
  display: grid;
  gap: 9px;
  margin-top: 14px;
}

.robot-action-delete-options > label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 13px;
  border: 1px solid #e7eaf1;
  border-radius: 9px;
  cursor: pointer;
}

.robot-action-delete-options input {
  margin-top: 3px;
}

.robot-action-delete-options strong,
.robot-action-delete-options small {
  display: block;
}

.robot-action-delete-options strong {
  font-size: 13px;
}

.robot-action-delete-options small {
  margin-top: 3px;
  color: #8a90a0;
  font-size: 11px;
  line-height: 1.5;
}

.robot-action-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid #eef0f5;
}

.robot-action-modal__footer button {
  height: 38px;
  min-width: 90px;
  border: 1px solid #dcdfe8;
  border-radius: 8px;
  background: #fff;
  font-size: 13.5px;
  font-weight: 700;
}

.robot-action-modal__footer button.primary {
  color: #fff;
  border-color: #1f6bff;
  background: #1f6bff;
}

.robot-action-modal__footer button.success {
  color: #fff;
  border-color: #0e9f5c;
  background: #0e9f5c;
}

.robot-action-modal__footer button.danger {
  color: #fff;
  border-color: #e5484d;
  background: #e5484d;
}

.robot-action-modal__footer button:disabled {
  cursor: not-allowed;
  opacity: .6;
}

@media (max-width: 600px) {
  .robot-action-grid {
    grid-template-columns: 1fr;
  }
}
</style>
